import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { toast } from 'react-toastify';
import './Credentials.css';

const Credentials = ({ teacherId, initialData = {} }) => {
  const [education, setEducation] = useState(initialData.education || []);
  const [experience, setExperience] = useState(initialData.experience || []);
  const [certifications, setCertifications] = useState(initialData.certifications || []);
  
  const [showEducationForm, setShowEducationForm] = useState(false);
  const [showExperienceForm, setShowExperienceForm] = useState(false);
  const [showCertificationForm, setShowCertificationForm] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // Education form state
  const [newEducation, setNewEducation] = useState({
    school: '',
    degree: '',
    field: '',
    startYear: '',
    endYear: '',
    description: ''
  });

  // Experience form state
  const [newExperience, setNewExperience] = useState({
    title: '',
    company: '',
    startDate: '',
    endDate: '',
    description: '',
    current: false
  });

  // Certification form state
  const [newCertification, setNewCertification] = useState({
    name: '',
    issuer: '',
    date: '',
    credentialId: ''
  });

  const addEducation = () => {
    if (!newEducation.school || !newEducation.degree) {
      toast.error('Please fill in required fields');
      return;
    }

    setEducation([...education, { ...newEducation, id: Date.now() }]);
    setNewEducation({
      school: '',
      degree: '',
      field: '',
      startYear: '',
      endYear: '',
      description: ''
    });
    setShowEducationForm(false);
    toast.success('Education added!');
  };

  const removeEducation = (id) => {
    setEducation(education.filter(item => item.id !== id));
  };

  const addExperience = () => {
    if (!newExperience.title || !newExperience.company) {
      toast.error('Please fill in required fields');
      return;
    }

    setExperience([...experience, { ...newExperience, id: Date.now() }]);
    setNewExperience({
      title: '',
      company: '',
      startDate: '',
      endDate: '',
      description: '',
      current: false
    });
    setShowExperienceForm(false);
    toast.success('Experience added!');
  };

  const removeExperience = (id) => {
    setExperience(experience.filter(item => item.id !== id));
  };

  const addCertification = () => {
    if (!newCertification.name || !newCertification.issuer) {
      toast.error('Please fill in required fields');
      return;
    }

    setCertifications([...certifications, { ...newCertification, id: Date.now() }]);
    setNewCertification({
      name: '',
      issuer: '',
      date: '',
      credentialId: ''
    });
    setShowCertificationForm(false);
    toast.success('Certification added!');
  };

  const removeCertification = (id) => {
    setCertifications(certifications.filter(item => item.id !== id));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'teachers', teacherId), {
        education,
        experience,
        certifications,
        updatedAt: new Date().toISOString()
      });
      toast.success('Credentials updated successfully!');
    } catch (error) {
      console.error('Save credentials error:', error);
      toast.error('Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="credentials-section">
      {/* Education Section */}
      <div className="credential-block">
        <div className="block-header">
          <h2>🎓 Education</h2>
          <button
            type="button"
            onClick={() => setShowEducationForm(!showEducationForm)}
            className="btn btn-secondary btn-sm"
          >
            {showEducationForm ? 'Cancel' : '+ Add Education'}
          </button>
        </div>

        {showEducationForm && (
          <div className="credential-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">School/University *</label>
                <input
                  type="text"
                  value={newEducation.school}
                  onChange={(e) => setNewEducation({ ...newEducation, school: e.target.value })}
                  className="form-input"
                  placeholder="e.g. University of Oxford"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Degree *</label>
                <input
                  type="text"
                  value={newEducation.degree}
                  onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })}
                  className="form-input"
                  placeholder="e.g. Bachelor's"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Field of Study</label>
              <input
                type="text"
                value={newEducation.field}
                onChange={(e) => setNewEducation({ ...newEducation, field: e.target.value })}
                className="form-input"
                placeholder="e.g. Computer Science"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Year</label>
                <input
                  type="number"
                  value={newEducation.startYear}
                  onChange={(e) => setNewEducation({ ...newEducation, startYear: e.target.value })}
                  className="form-input"
                  placeholder="2015"
                />
              </div>
              <div className="form-group">
                <label className="form-label">End Year</label>
                <input
                  type="number"
                  value={newEducation.endYear}
                  onChange={(e) => setNewEducation({ ...newEducation, endYear: e.target.value })}
                  className="form-input"
                  placeholder="2019"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newEducation.description}
                onChange={(e) => setNewEducation({ ...newEducation, description: e.target.value })}
                className="form-textarea"
                rows="3"
                placeholder="Brief description..."
              />
            </div>

            <button type="button" onClick={addEducation} className="btn btn-primary">
              Add Education
            </button>
          </div>
        )}

        <div className="credential-list">
          {education.map((item) => (
            <div key={item.id} className="credential-item">
              <div className="item-content">
                <h3>{item.degree} {item.field && `in ${item.field}`}</h3>
                <p className="item-subtitle">{item.school}</p>
                {(item.startYear || item.endYear) && (
                  <p className="item-date">
                    {item.startYear} - {item.endYear || 'Present'}
                  </p>
                )}
                {item.description && <p className="item-description">{item.description}</p>}
              </div>
              <button
                type="button"
                onClick={() => removeEducation(item.id)}
                className="remove-item-btn"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Experience Section */}
      <div className="credential-block">
        <div className="block-header">
          <h2>💼 Work Experience</h2>
          <button
            type="button"
            onClick={() => setShowExperienceForm(!showExperienceForm)}
            className="btn btn-secondary btn-sm"
          >
            {showExperienceForm ? 'Cancel' : '+ Add Experience'}
          </button>
        </div>

        {showExperienceForm && (
          <div className="credential-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Job Title *</label>
                <input
                  type="text"
                  value={newExperience.title}
                  onChange={(e) => setNewExperience({ ...newExperience, title: e.target.value })}
                  className="form-input"
                  placeholder="e.g. English Teacher"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Company *</label>
                <input
                  type="text"
                  value={newExperience.company}
                  onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })}
                  className="form-input"
                  placeholder="e.g. ABC School"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="month"
                  value={newExperience.startDate}
                  onChange={(e) => setNewExperience({ ...newExperience, startDate: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="month"
                  value={newExperience.endDate}
                  onChange={(e) => setNewExperience({ ...newExperience, endDate: e.target.value })}
                  className="form-input"
                  disabled={newExperience.current}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newExperience.current}
                  onChange={(e) => setNewExperience({ ...newExperience, current: e.target.checked, endDate: '' })}
                />
                <span>I currently work here</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newExperience.description}
                onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })}
                className="form-textarea"
                rows="3"
                placeholder="Describe your responsibilities..."
              />
            </div>

            <button type="button" onClick={addExperience} className="btn btn-primary">
              Add Experience
            </button>
          </div>
        )}

        <div className="credential-list">
          {experience.map((item) => (
            <div key={item.id} className="credential-item">
              <div className="item-content">
                <h3>{item.title}</h3>
                <p className="item-subtitle">{item.company}</p>
                {(item.startDate || item.endDate || item.current) && (
                  <p className="item-date">
                    {item.startDate} - {item.current ? 'Present' : item.endDate}
                  </p>
                )}
                {item.description && <p className="item-description">{item.description}</p>}
              </div>
              <button
                type="button"
                onClick={() => removeExperience(item.id)}
                className="remove-item-btn"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Certifications Section */}
      <div className="credential-block">
        <div className="block-header">
          <h2>🏆 Certifications</h2>
          <button
            type="button"
            onClick={() => setShowCertificationForm(!showCertificationForm)}
            className="btn btn-secondary btn-sm"
          >
            {showCertificationForm ? 'Cancel' : '+ Add Certification'}
          </button>
        </div>

        {showCertificationForm && (
          <div className="credential-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Certification Name *</label>
                <input
                  type="text"
                  value={newCertification.name}
                  onChange={(e) => setNewCertification({ ...newCertification, name: e.target.value })}
                  className="form-input"
                  placeholder="e.g. TEFL Certificate"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Issuing Organization *</label>
                <input
                  type="text"
                  value={newCertification.issuer}
                  onChange={(e) => setNewCertification({ ...newCertification, issuer: e.target.value })}
                  className="form-input"
                  placeholder="e.g. Cambridge"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input
                  type="month"
                  value={newCertification.date}
                  onChange={(e) => setNewCertification({ ...newCertification, date: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Credential ID</label>
                <input
                  type="text"
                  value={newCertification.credentialId}
                  onChange={(e) => setNewCertification({ ...newCertification, credentialId: e.target.value })}
                  className="form-input"
                  placeholder="Optional"
                />
              </div>
            </div>

            <button type="button" onClick={addCertification} className="btn btn-primary">
              Add Certification
            </button>
          </div>
        )}

        <div className="credential-list">
          {certifications.map((item) => (
            <div key={item.id} className="credential-item">
              <div className="item-content">
                <h3>{item.name}</h3>
                <p className="item-subtitle">{item.issuer}</p>
                {item.date && <p className="item-date">{item.date}</p>}
                {item.credentialId && (
                  <p className="item-credential">Credential ID: {item.credentialId}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeCertification(item.id)}
                className="remove-item-btn"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="save-section">
        <button
          type="button"
          onClick={handleSave}
          className="btn btn-primary btn-lg"
          disabled={loading}
        >
          {loading ? <div className="spinner"></div> : '💾 Save All Credentials'}
        </button>
      </div>
    </div>
  );
};

export default Credentials;
